import { useEffect } from "react";

import { EventProps } from "@utils/enums/event";

type EventList = keyof EventProps;

type CustomEvent<EventName extends EventList> = Event & {
  detail: EventProps[EventName];
};

type EventCallback<EventName extends EventList> = (e: CustomEvent<EventName>) => void;

const dispatchGlobalEvent = <EventName extends EventList>(
  event: EventName,
  data: EventProps[EventName]
) => {
  if (typeof document === "undefined") return;

  document.dispatchEvent(
    new CustomEvent(event, {
      detail: data,
    })
  );
};

/**
 * Hook create a global event
 * @param {string} event The KEY event
 * @returns
 */
function useEvent<EventName extends EventList>(event: EventName) {
  /**
   * Dispatcher data event global
   * @param {any} data
   */
  const dispatch = (data: EventProps[EventName]) => {
    dispatchGlobalEvent(event, data);
  };

  /**
   * The listener a event pushing by dispatch
   * @param {void} callback
   */
  const listener = (callback: EventCallback<EventName>) => {
    if (typeof document === "undefined") return;
    document.addEventListener(event, callback as EventListenerOrEventListenerObject);
  };

  /**
   * Destroy a event listening
   * @param {void} callback
   */
  const remove = (callback: EventCallback<EventName>) => {
    if (typeof document === "undefined") return;

    document.removeEventListener(event, callback as EventListenerOrEventListenerObject);
  };

  return { dispatch, listener, remove };
}

function useEventListener<EventName extends EventList>(
  event: EventName,
  callback: EventCallback<EventName>,
  deps: unknown[]
) {
  const { listener, remove } = useEvent(event);

  useEffect(() => {
    listener(callback);

    return () => {
      remove(callback);
    };
  }, [event, ...deps]);

  return null;
}

export { dispatchGlobalEvent, useEvent, useEventListener };
